import { NgTemplateOutlet } from '@angular/common';
import type { TemplateRef } from '@angular/core';
import { Component, computed, contentChild, effect, inject, input, signal } from '@angular/core';

import { ThemeService } from 'src/services/theme/theme.service';
import type { TFullColorScheme } from 'src/shared/types/color-schemes-and-themes';

import { ELayoutType } from '../../entities/types';
import type { Widget, WidgetInputData } from '../../entities/types';
import type { WidgetLayoutEntry } from '../../entities/types';
import { buildWidgetLayoutEntries } from '../../helpers/widget-spacing.utils';
import { WidgetLoaderComponent } from '../widget-loader/widget-loader.component';

@Component({
    selector: 'website-widgets-loader',
    templateUrl: './widgets-loader.component.html',
    imports: [NgTemplateOutlet, WidgetLoaderComponent],
})
export class WidgetsLoaderComponent {
    private readonly themeService = inject(ThemeService);

    widgets = input<(Widget & WidgetInputData)[]>([]);
    layoutType = input<ELayoutType>(ELayoutType.Full);

    widgetTemplateRef = contentChild<TemplateRef<unknown>>('widget');

    public readonly widgetLayoutEntries = signal<WidgetLayoutEntry[]>([]);

    private readonly widgetsNotSupportedReduceSpacings = ['pricing_widget'];

    readonly isSidebarLayout = computed(() => this.layoutType() === ELayoutType.Sidebar);

    constructor() {
        effect(() => {
            this.widgets();
            this.layoutType();
            this.rebuildWidgetLayoutEntries();
        });
    }

    public getWidgetColorScheme(widget: Widget & WidgetInputData): TFullColorScheme | undefined {
        if (widget?.viewOptions?.viewType === 'banner') {
            return this.themeService.isCollaborate(widget?.commonWidgetData?.colorScheme)
                ? widget?.viewOptions?.underlayColor
                : widget?.commonWidgetData?.colorScheme;
        }

        return widget?.commonWidgetData?.colorScheme;
    }

    private rebuildWidgetLayoutEntries(): void {
        this.widgetLayoutEntries.set(
            buildWidgetLayoutEntries({
                widgets: this.widgets(),
                isSidebarLayout: this.isSidebarLayout(),
                widgetsNotSupportedReduceSpacings: this.widgetsNotSupportedReduceSpacings,
                getWidgetColorScheme: (widget) => this.getWidgetColorScheme(widget),
            })
        );
    }
}
